// A stream error that arrived AFTER the answer had already committed text,
// sections or charts to the screen.
//
// Incident 2026-08-07: a fully rendered answer was REPLACED by the error bubble
// when a late SSE `error` event landed, destroying work the user could still
// read. Committed content now stays put and the error is shown as this notice
// underneath it. The bare error bubble (Message.tsx / AssistantMessage.tsx)
// remains the rendering for a turn that produced nothing worth keeping.
import { ActionIcon, Alert, Group, Text } from "@mantine/core";
import { IconAlertCircle, IconRefresh } from "@tabler/icons-react";

import { ERROR_HINTS, INFO_ERROR_CODES } from "./errorHints";

export interface StreamErrorNoticeProps {
  /** Backend error code — drives the hint text and the info/error colouring. */
  code?: string;
  message: string;
  /** Hides the retry affordance when the backend says retrying won't help. */
  recoverable?: boolean;
  /** Omitted when the surface has no retry path — the notice still renders. */
  onRetry?: () => void;
}

export default function StreamErrorNotice({
  code,
  message,
  recoverable,
  onRetry,
}: StreamErrorNoticeProps) {
  const color = code && INFO_ERROR_CODES.has(code) ? "yellow" : "red";
  const hint = code ? ERROR_HINTS[code] : undefined;

  return (
    <Alert
      icon={<IconAlertCircle size={18} />}
      title="This answer stopped early"
      color={color}
      variant="light"
      mt="xs"
    >
      <Text size="sm" style={{ lineHeight: 1.6 }}>
        {message}
      </Text>
      <Text size="xs" c="dimmed" mt={4}>
        The part shown above finished before the error.
      </Text>
      {hint && (
        <Text size="xs" c="dimmed" mt="xs">
          {hint}
        </Text>
      )}
      {recoverable !== false && onRetry && (
        <Group mt="sm">
          <ActionIcon
            variant="light"
            color={color}
            size="sm"
            aria-label="Try again"
            onClick={onRetry}
          >
            <IconRefresh size={14} />
          </ActionIcon>
          <Text size="xs" c="dimmed">
            Try again
          </Text>
        </Group>
      )}
    </Alert>
  );
}
